import { Amount, Percent, Time, type AbsoluteAmount } from '@df/common';
import { z } from 'zod';
import { zodDecoder } from '$lib/services/api/zod_decoder';
import { accountGroupSchema, mapFromRecord, objectMapFromRecord } from './schemas';

export const benchmarks = [
  'Revenue',
  'GrossProfit',
  'Ebitda',
  'FinancialNetPlusOwnerSalaries',
  'FinancialNet',
  'TotalAssets',
  'Equity',
  'OtherBenchmark',
] as const;
const benchmarkSchema = z.enum(benchmarks);
export type Benchmark = z.infer<typeof benchmarkSchema>;

const materialityBasisSchema = z.enum(['PerformanceMateriality', 'TotalAssets']);
type MaterialityBasis = z.infer<typeof materialityBasisSchema>;

export const materialityBenchmarksSchema = z.object({
  revenue: Amount.schema,
  grossProfit: Amount.schema,
  ebitda: Amount.schema,
  financialNet: Amount.schema,
  equity: Amount.schema,
  totalAssets: Amount.schema,
  ownerSalary: Amount.schema,
});
export type MaterialityBenchmarks = z.infer<typeof materialityBenchmarksSchema>;
export const materialityBenchmarksDecoder = zodDecoder(materialityBenchmarksSchema);

const amountPercentSchema = z.object({
  amount: Amount.schema,
  percent: Percent.schema,
});
export type AmountPercent = z.infer<typeof amountPercentSchema>;

export type MaterialityLevel =
  | {
      type: 'Amount';
      amount: AbsoluteAmount;
    }
  | { type: 'Percent'; percent: Percent };

export type Levels = {
  materiality: MaterialityLevel;
  performanceMateriality: MaterialityLevel;
  classificationError: MaterialityLevel;
  clearlyTrivial: MaterialityLevel;
  transactionNote: MaterialityLevel;
};

export const materialityLevelsSchema = z.object({
  motivation: z.string().optional(),
  benchmark: z.object({
    amount: Amount.schema,
    reason: benchmarkSchema,
  }), // Referensvärde
  materiality: amountPercentSchema, // Väsentlighet
  performanceMateriality: amountPercentSchema, // Arbetsväsentlighet
  classificationError: amountPercentSchema, // Klassificerings fel
  clearlyTrivial: amountPercentSchema, // Gräns för noteringar
  transactionNote: amountPercentSchema, // Gräns för bokföringsposter
  materialAccountThreshold: z.object({
    amount: Amount.schema,
    reason: materialityBasisSchema,
  }), // Gräns för granskning
});
export type AbsoluteMaterialityLevels = z.infer<typeof materialityLevelsSchema>;
export const materialityLevelsDecoder = zodDecoder(materialityLevelsSchema);

/** A year whose case has no materiality set is absent. */
export const clientMaterialityLevelsSchema = mapFromRecord(
  Time.BookYear.schema,
  materialityLevelsSchema,
); // key = BookYear
export type ClientMaterialityLevels = z.infer<typeof clientMaterialityLevelsSchema>;
export const clientMaterialityLevelsDecoder = zodDecoder(clientMaterialityLevelsSchema);

export type MaterialityLevels = {
  materialAccountThresholdBasis: MaterialityBasis;
  benchmark: Benchmark;
  materiality: Percent;
  performanceMateriality: Percent;
  classificationError: Percent;
  clearlyTrivial: Percent;
  transactionNote: Percent;
  motivation?: string;
  customBenchmark?: Amount;
};

const materialityOverridesSchema = z.object({
  makeMaterial: objectMapFromRecord(accountGroupSchema, z.string()),
  skipMaterial: objectMapFromRecord(accountGroupSchema, z.string()),
});
export type MaterialityOverrides = z.infer<typeof materialityOverridesSchema>;
export const materialityOverridesDecoder = zodDecoder(materialityOverridesSchema);

const materialityOverridesMakeMaterialSchema = z.object({
  type: z.literal('MakeMaterial'),
  group: accountGroupSchema,
  motivation: z.string(),
});
export type MaterialityOverridesMakeMaterial = z.infer<
  typeof materialityOverridesMakeMaterialSchema
>;

const materialityOverridesMakeNonMaterialSchema = z.object({
  type: z.literal('MakeNonMaterial'),
  group: accountGroupSchema,
  motivation: z.string(),
});

const materialityOverridesUseSystemSuggestionSchema = z.object({
  type: z.literal('UseSystemSuggestion'),
  group: accountGroupSchema,
});
export type MaterialityOverridesUseSystemSuggestion = z.infer<
  typeof materialityOverridesUseSystemSuggestionSchema
>;

const materialityOverridesUpdateSchema = z.discriminatedUnion('type', [
  materialityOverridesMakeMaterialSchema,
  materialityOverridesMakeNonMaterialSchema,
  materialityOverridesUseSystemSuggestionSchema,
]);
export type MaterialityOverridesUpdate = z.infer<typeof materialityOverridesUpdateSchema>;
